import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../api/client';
import { VIDEO_UPLOAD_LIMIT_TEXT, SOURCE_LANGUAGE_OPTIONS, AUTO_LANGUAGE } from '@captionflow/shared';

import { validateVideoUpload } from '../lib/validateVideoUpload';

type Phase = 'validating' | 'queued' | 'uploading' | 'processing' | 'ready' | 'failed';
interface Item {
  key: string;
  name: string;
  projectId?: string;
  phase: Phase;
  uploadPct: number;
  pipeline: Record<string, { status: string; detail?: string }>;
  error?: string;
}

const STEP_LABELS: [string, string][] = [
  ['upload', 'Upload'],
  ['proxy', 'Preview'],
  ['transcribe', 'Transcribe'],
  ['enhance', 'Auto-enhance'],
];

/**
 * New-project flow as a modal over the dashboard: drop one clip, which runs the
 * full pipeline (upload → proxy → detect+transcribe → auto-enhance) with live
 * progress. A ready project refreshes the dashboard grid (invalidates the
 * `projects` query) and links straight in.
 */
export function NewProjectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [items, setItems] = useState<Item[]>([]);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Spoken language for these uploads' transcription. Defaults to the first
  // supported language (no "Auto" — STT needs a concrete primary language).
  // A ref so runOne always reads the current value without re-creating.
  const [uploadLang, setUploadLang] = useState(AUTO_LANGUAGE.code);
  const langRef = useRef(AUTO_LANGUAGE.code);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const update = useCallback((key: string, patch: Partial<Item>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }, []);

  // Close on Escape; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const runOne = useCallback(
    async (file: File, key: string) => {
      try {
        update(key, { phase: 'validating' });
        const durationMs = await validateVideoUpload(file);
        const name = file.name.replace(/\.[^.]+$/, '') || 'Untitled';
        const project = await api.createProject(name, '9:16', langRef.current || undefined);
        update(key, { projectId: project.id, phase: 'uploading' });
        // A freshly-created project already belongs on the grid.
        void qc.invalidateQueries({ queryKey: ['projects'] });

        const contentType = file.type || 'video/mp4';
        const up = await api.requestUpload(project.id, file.name, contentType, file.size, durationMs);
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', up.url);
          xhr.setRequestHeader('Content-Type', contentType);
          xhr.upload.onprogress = (e) =>
            e.lengthComputable && update(key, { uploadPct: e.loaded / e.total });
          xhr.onload = () =>
            xhr.status >= 200 && xhr.status < 300
              ? resolve()
              : reject(new Error(`Upload failed (${xhr.status})`));
          xhr.onerror = () => reject(new Error('Upload failed'));
          xhr.send(file);
        });
        update(key, { uploadPct: 1, phase: 'processing' });
        await api.confirmUpload(project.id, up.uploadId);

        for (let i = 0; i < 200; i++) {
          await new Promise((r) => setTimeout(r, 1500));
          const p = await api.getPipeline(project.id);
          update(key, { pipeline: p.pipeline });
          if (p.overall === 'ready') {
            void qc.invalidateQueries({ queryKey: ['projects'] });
            return update(key, { phase: 'ready' });
          }
          if (p.overall === 'failed')
            return update(key, { phase: 'failed', error: Object.values(p.pipeline).find((step) => step.status === 'failed')?.detail || 'A step failed' });
        }
        update(key, { phase: 'failed', error: 'Timed out' });
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 401) {
          setNeedsAuth(true);
          return;
        }
        update(key, { phase: 'failed', error: err instanceof Error ? err.message : 'Failed' });
      }
    },
    [update, qc],
  );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      // One clip per project: take the first video and ignore the rest.
      const file = Array.from(files).find((f) => f.type.startsWith('video/'));
      if (!file) return;
      const item: Item = {
        key: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: file.name,
        phase: 'queued',
        uploadPct: 0,
        pipeline: {},
      };
      setItems([item]);
      void runOne(file, item.key);
    },
    [runOne],
  );

  if (!open) return null;

  const busy = items.some((it) => it.phase !== 'ready' && it.phase !== 'failed');

  return (
    <div className="np-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="np-modal" role="dialog" aria-modal="true" aria-label="New project">
        <div className="np-head">
          <div>
            <h2>New project</h2>
            <p>Drop your clip - transcribed, auto-enhanced and made ready to edit.</p>
          </div>
          <button className="np-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {needsAuth ? (
          <div className="np-auth">
            Please <Link to="/login">sign in</Link> to create projects.
          </div>
        ) : (
          <>
            <label className="np-lang">
              <span>Spoken language</span>
              <select
                value={uploadLang}
                onChange={(e) => {
                  setUploadLang(e.target.value);
                  langRef.current = e.target.value;
                }}
              >
                {SOURCE_LANGUAGE_OPTIONS.map((l) => (
                  <option key={`${l.label}-${l.code}`} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
            </label>
            <div
              className={dragOver ? 'np-drop over' : 'np-drop'}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              onClick={() => inputRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              <span className="np-drop-icon" aria-hidden="true">
                ↑
              </span>
              <strong>Drop videos here</strong>
              <span className="np-drop-sub">or click to choose · {VIDEO_UPLOAD_LIMIT_TEXT}</span>
              <input
                ref={inputRef}
                type="file"
                accept="video/*"
                hidden
                onChange={(e) => e.target.files && addFiles(e.target.files)}
              />
            </div>

            {items.length > 0 && (
              <div className="np-list">
                {items.map((it) => (
                  <div key={it.key} className={`np-card ${it.phase}`}>
                    <div className="np-card-head">
                      <strong className="np-card-name">{it.name}</strong>
                      {it.phase === 'ready' && it.projectId && (
                        <button
                          className="np-open"
                          onClick={() => navigate(`/studio/${it.projectId}`)}
                        >
                          Open studio →
                        </button>
                      )}
                      {it.phase === 'failed' && (
                        <span className="np-card-fail">
                          Failed{it.error ? `: ${it.error}` : ''}
                        </span>
                      )}
                    </div>
                    <div className="np-steps">
                      {STEP_LABELS.map(([keyName, label]) => {
                        const st =
                          keyName === 'upload' && it.phase === 'uploading'
                            ? 'running'
                            : (it.pipeline[keyName]?.status ?? 'pending');
                        const detail = it.pipeline[keyName]?.detail;
                        return (
                          <div key={keyName} className={`np-step ${st}`}>
                            <span className="np-step-dot" />
                            <span className="np-step-label">
                              {keyName === 'upload' && it.phase === 'validating' ? 'Checking video…' : label}
                              {keyName === 'upload' && it.phase === 'uploading' && it.uploadPct > 0
                                ? ` ${Math.round(it.uploadPct * 100)}%`
                                : ''}
                              {detail && st === 'done' ? ` · ${detail}` : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="np-foot">
              <button className="np-done" onClick={onClose}>
                {busy ? 'Continue in background' : 'Done'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
